import { FC, useRef, useEffect } from "react";
import clsx from "classnames";
import { useStyles } from "./NotchItem.style";
import { Image } from "@onmo/ui";
import { assetsImages } from "../../../models/theme";
import {
  AUTO_HIDE_DELAY,
  HIDDEN_OFFSET,
  VISIBLE_OFFSET,
  CLICK_THRESHOLD,
} from "../../../constants/notch";

interface IArrowCollapseProps {
  onClose?: () => void;
  open?: boolean;
}

export const NotchItem: FC<IArrowCollapseProps> = (props) => {
  const classes = useStyles();
  const { onClose, open } = props;

  const animationFrameRef = useRef(0);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const notchRef = useRef<HTMLDivElement>(null);

  const sideRef = useRef<"left" | "right">("left");
  const isHiddenRef = useRef(false);
  const topRef = useRef(30);

  const hideNotch = () => {
    const elmnt = notchRef.current;

    if (!elmnt) return;
    elmnt.style.transition = "left 250ms ease-out";

    elmnt.style.left =
      sideRef.current === "left"
        ? `-${HIDDEN_OFFSET}px`
        : `${window.innerWidth - elmnt.offsetWidth + HIDDEN_OFFSET}px`;

    isHiddenRef.current = true;
  };

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const startHideTimer = () => {
    clearHideTimeout();

    hideTimeoutRef.current = setTimeout(() => {
      hideNotch();
    }, AUTO_HIDE_DELAY);
  };

  const showNotch = () => {
    const elmnt = notchRef.current;

    if (!elmnt) return;
    elmnt.style.transition = "left 250ms ease-out";

    elmnt.style.left =
      sideRef.current === "left"
        ? `${VISIBLE_OFFSET}px`
        : `${window.innerWidth - elmnt.offsetWidth - VISIBLE_OFFSET}px`;

    isHiddenRef.current = false;
  };

  const handleNotchClick = () => {
    const elmnt = notchRef.current;
    if (!elmnt) return;

    if (isHiddenRef.current) {
      showNotch();
      startHideTimer();
      return;
    }

    clearHideTimeout();
    hideNotch();
    onClose?.();
  };

  useEffect(() => {
    const dragElement = (elmnt: HTMLDivElement) => {
      let pos1 = 0,
        pos2 = 0,
        pos3 = 0,
        pos4 = 0;

      let dragDistance = 0;

      const getClientXY = (e: MouseEvent | TouchEvent) => {
        if ("touches" in e) {
          return { x: e.touches[0].clientX, y: e.touches[0].clientY };
        } else {
          return { x: e.clientX, y: e.clientY };
        }
      };

      const dragStart = (e: MouseEvent | TouchEvent) => {
        elmnt.style.transition = "none";
        clearHideTimeout();
        e.preventDefault();
        dragDistance = 0;
        const { x, y } = getClientXY(e);
        pos3 = x;
        pos4 = y;

        document.onmouseup = dragEnd;
        document.onmousemove = dragging;
        document.ontouchend = dragEnd;
        document.ontouchmove = dragging;
      };

      const dragging = (e: MouseEvent | TouchEvent) => {
        const { x, y } = getClientXY(e);
        pos1 = pos3 - x;
        pos2 = pos4 - y;
        pos3 = x;
        pos4 = y;

        dragDistance += Math.abs(pos1) + Math.abs(pos2);

        const newTop = elmnt.offsetTop - pos2;

        elmnt.style.top = `${newTop}px`;
        elmnt.style.left = `${elmnt.offsetLeft - pos1}px`;

        topRef.current = newTop;
      };

      const dragEnd = () => {
        elmnt.style.transition = "none";
        document.onmouseup = null;
        document.onmousemove = null;
        document.ontouchend = null;
        document.ontouchmove = null;

        if (dragDistance < CLICK_THRESHOLD ) {
          handleNotchClick();
          return;
        }

        const screenWidth = window.innerWidth;
        const isLeft = elmnt.offsetLeft < screenWidth / 2;
        sideRef.current = isLeft ? "left" : "right";

        const moveTo = isLeft
          ? -HIDDEN_OFFSET
          : screenWidth - elmnt.offsetWidth + HIDDEN_OFFSET;

        const smoothMove = () => {
          const currentLeft = elmnt.offsetLeft;
          const distance = moveTo - currentLeft;
          const speed = distance * 0.2;

          if (elmnt.offsetTop < 30) {
            elmnt.style.top = `30px`;
          }
          if (elmnt.offsetTop > window.innerHeight - 50) {
            elmnt.style.top = `${window.innerHeight - 50}px`;
          }

          if (Math.abs(distance) > 2) {
            elmnt.style.left = `${currentLeft + speed}px`;
            animationFrameRef.current = requestAnimationFrame(smoothMove);
          } else {
            elmnt.style.left = `${moveTo}px`;
            isHiddenRef.current = true;
            clearHideTimeout();
            cancelAnimationFrame(animationFrameRef.current);
          }
        };

        animationFrameRef.current = requestAnimationFrame(smoothMove);
      };

      elmnt.ontouchstart = dragStart;
      elmnt.onmousedown = dragStart;
    };

    const elmnt = notchRef.current;
    if (elmnt) {
      dragElement(elmnt);
      startHideTimer();
    }

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const elmnt = notchRef.current;

      if (!elmnt) return;

      const notchWidth = elmnt.offsetWidth || 50;
      const top = Math.min(
        Math.max(topRef.current, 30),
        window.innerHeight - 50,
      );
      elmnt.style.top = `${top}px`;

      if (isHiddenRef.current) {
        elmnt.style.left =
          sideRef.current === "left"
            ? `-${HIDDEN_OFFSET}px`
            : `${window.innerWidth - notchWidth + HIDDEN_OFFSET}px`;
      } else {
        elmnt.style.left =
          sideRef.current === "left"
            ? `${VISIBLE_OFFSET}px`
            : `${window.innerWidth - notchWidth - VISIBLE_OFFSET}px`;
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Clean up animation frames
  useEffect(() => {
    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      clearHideTimeout();
    };
  }, []);

  return (
    <div
      ref={notchRef}
      id="notch-logo-btn"
      className={clsx(classes.logoIcon, { open })}
    >
      <Image src={assetsImages.notchBadgeLogo} width={50} height={50} />
    </div>
  );
};
